/**
 * Send-time roster confirmation for the whole-house composer.
 *
 * When the host presses Send, `useBroadcastRosterReview` becomes active and
 * immediately confirms the residents of every selected house with PadSplit:
 * it runs the server roster check, and unless every house's member data was
 * harvested within the last few minutes it asks PadSplit for a fresh members
 * pass and polls until each house's data is newer than the press (bounded).
 * Newly reported residents refresh the review directory but never expand
 * consent. Final recipient confirmation is always explicit in the composer.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage, queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertCircle, AlertTriangle, CheckCircle2, Loader2, UserPlus } from "lucide-react";
import {
  isHouseConfirmed,
  type AutoIncludedMember,
  type PropertyRosterCheck,
  type RosterCheckResult,
  type RosterGap,
  type RosterGapKind,
} from "@shared/broadcast-roster";

export const ROSTER_CHECK_URL = "/api/app/messages/broadcast/roster-check";
export const ROSTER_CONFIRM_POLL_MS = 5_000;
export const ROSTER_REVIEW_DEADLINE_MS = 75_000;

export type RosterConfirmPhase = "idle" | "checking" | "refreshing" | "waiting" | "confirmed" | "timeout" | "error";
export type HouseConfirmState = "confirmed" | "waiting" | "no_data";

const GAP_LABELS: Record<RosterGapKind, string> = {
  occupied_room_no_member: "Looks occupied, no member",
  unlisted_room_no_member: "Unlisted, nobody assigned",
  property_conflict: "Property conflict",
  duplicate_room: "Duplicate room",
  missing_host_link: "No host link",
  omitted_member: "Deselected",
  stale_data: "Stale data",
  no_member_data: "No member data",
};

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** A house with no members on record has nothing PadSplit can confirm; it stays a reviewed gap. */
export function houseConfirmState(p: PropertyRosterCheck, pressedAt: number): HouseConfirmState {
  if (p.gaps.some((g) => g.kind === "no_member_data")) return "no_data";
  return isHouseConfirmed(p.confirmedHarvestAt, pressedAt) ? "confirmed" : "waiting";
}

function allHousesConfirmed(data: RosterCheckResult, pressedAt: number): boolean {
  return data.properties.every((p) => houseConfirmState(p, pressedAt) !== "waiting");
}

export function formatElapsed(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m > 0 ? `${m}m ${String(s).padStart(2, "0")}s` : `${s}s`;
}

export function useBroadcastRosterReview({
  active,
  propertyIds,
  memberIds,
  excludedMemberIds,
  audienceMode = "whole_house",
  reviewedRecipients,
  pollMs = ROSTER_CONFIRM_POLL_MS,
  timeoutMs = ROSTER_REVIEW_DEADLINE_MS,
}: {
  active: boolean;
  propertyIds: number[];
  memberIds: number[];
  /** Members deliberately left out. New arrivals also require explicit review. */
  excludedMemberIds?: number[];
  reviewedRecipients?: { id: number; reviewKey: string }[];
  audienceMode?: "whole_house" | "selected_residents";
  pollMs?: number;
  timeoutMs?: number;
}) {
  const sortedProps = [...propertyIds].sort((a, b) => a - b);
  const sortedMembers = [...memberIds].sort((a, b) => a - b);
  const sortedExcluded = excludedMemberIds ? [...excludedMemberIds].sort((a, b) => a - b) : null;
  const sortedReviewed = reviewedRecipients ? [...reviewedRecipients].sort((a, b) => a.id - b.id) : null;
  const selectionKey = JSON.stringify([sortedProps, sortedMembers, sortedExcluded, audienceMode, sortedReviewed]);
  const selectionRef = useRef(selectionKey);
  selectionRef.current = selectionKey;
  const [confirmedSelection, setConfirmedSelection] = useState<string | null>(null);
  const query = useQuery<RosterCheckResult & { confirmation?: { state: string; message: string } }>({
    queryKey: [ROSTER_CHECK_URL, sortedProps, sortedMembers, sortedExcluded, audienceMode, sortedReviewed],
    queryFn: async ({ signal }) => {
      const res = await apiRequest(
        "POST",
        ROSTER_CHECK_URL,
        {
          propertyIds: sortedProps,
          memberIds: sortedMembers,
          audienceMode,
          ...(sortedReviewed ? { reviewedRecipients: sortedReviewed } : {}),
          ...(sortedExcluded ? { excludedMemberIds: sortedExcluded } : {}),
        },
        { signal },
      );
      return (await res.json()) as RosterCheckResult;
    },
    // The bounded confirmation run owns fetching, including the initial request.
    enabled: false,
    staleTime: 0,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
  });

  // Acknowledgement is bound to one fingerprint: any change in the gap set
  // (re-check, fresh PadSplit data, selection edit) silently invalidates it.
  const [ackFingerprint, setAckFingerprint] = useState<string | null>(null);
  const data = query.data;
  const acknowledged = !!data && ackFingerprint === data.fingerprint;

  const [phase, setPhase] = useState<RosterConfirmPhase>("idle");
  const [message, setMessage] = useState<string | null>(null);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [pressedAt, setPressedAt] = useState<number | null>(null);
  const [addedMembers, setAddedMembers] = useState<AutoIncludedMember[]>([]);
  const runIdRef = useRef(0);
  const stopRunRef = useRef<() => void>(() => {});
  const refetchRef = useRef(query.refetch);
  refetchRef.current = query.refetch;

  const absorb = useCallback((next: RosterCheckResult) => {
    const added = next.autoIncludedMembers ?? [];
    if (added.length === 0) return;
    setAddedMembers((prev) => {
      const known = new Set(prev.map((m) => m.memberId));
      const fresh = added.filter((m) => !known.has(m.memberId));
      return fresh.length > 0 ? [...prev, ...fresh] : prev;
    });
    // Refresh the directory for review only. The picker never auto-selects arrivals.
    void queryClient.invalidateQueries({ queryKey: ["/api/app/messages/broadcast/members"] });
  }, []);

  const runConfirmation = useCallback(
    async (pressed: number) => {
      stopRunRef.current();
      const runId = ++runIdRef.current;
      const selection = selectionRef.current;
      const live = () => runIdRef.current === runId && selectionRef.current === selection;
      const cancelCheck = () => {
        void queryClient.cancelQueries({
          queryKey: [ROSTER_CHECK_URL, ...JSON.parse(selection)],
          exact: true,
        });
      };
      const elapsed = setInterval(() => {
        if (live()) setElapsedSec(Math.floor((Date.now() - pressed) / 1000));
      }, 1_000);
      // Covers the FIRST check, harvest HTTP, subsequent checks, and sleeps.
      // This timer does not rely on fetch resolving or honoring AbortSignal.
      const deadline = setTimeout(() => {
        if (!live()) return;
        runIdRef.current += 1;
        clearInterval(elapsed);
        cancelCheck();
        setPhase("timeout");
        setMessage("Nothing was sent. PadSplit did not confirm residents within the time limit. Try the resident check again or close and return later.");
      }, Math.min(timeoutMs, ROSTER_REVIEW_DEADLINE_MS));
      let stopped = false;
      const stop = () => {
        if (stopped) return;
        stopped = true;
        clearTimeout(deadline);
        clearInterval(elapsed);
        cancelCheck();
      };
      stopRunRef.current = stop;
      setPhase("checking");
      setMessage(null);
      setConfirmedSelection(null);
      setAckFingerprint(null);
      try {
      const first = await refetchRef.current();
      if (!live()) return;
      if (!first.data || first.isError) {
        setPhase("error");
        setMessage("Nothing was sent. Couldn't check who lives at these houses. Try the resident check again.");
        return;
      }
      absorb(first.data);
      if (first.data.confirmation && first.data.confirmation.state !== "checking") {
        setMessage(first.data.confirmation.message);
        if (first.data.confirmation.state === "confirmed") {
          setConfirmedSelection(selection);
          setPhase("confirmed");
        } else {
          setPhase("error");
        }
        return;
      }
      if (!first.data.confirmation && allHousesConfirmed(first.data, Date.now())) {
        setConfirmedSelection(selection);
        setPhase("confirmed");
        return;
      }
      // Never start an account-wide harvest from a send-time check.
      if (!live()) return;
      setPhase("waiting");
      while (live()) {
        await sleep(pollMs);
        if (!live()) return;
        setElapsedSec(Math.round((Date.now() - pressed) / 1000));
        const next = await refetchRef.current();
        if (!live()) return;
        if (next.isError) {
          setPhase("error");
          setMessage("Nothing was sent. The resident check failed. Try again.");
          return;
        }
        if (next.data) {
          absorb(next.data);
          if (next.data.confirmation && next.data.confirmation.state !== "checking") {
            setMessage(next.data.confirmation.message);
            if (next.data.confirmation.state === "confirmed") {
              setConfirmedSelection(selection);
              setPhase("confirmed");
            } else {
              setPhase("error");
            }
            return;
          }
          if (!next.data.confirmation && allHousesConfirmed(next.data, Date.now())) {
            setConfirmedSelection(selection);
            setPhase("confirmed");
            return;
          }
        }
      }
      } catch (err) {
        if (!live()) return;
        setPhase("error");
        setMessage(parseErrorMessage(err, "Nothing was sent. The resident check failed."));
      } finally {
        stop();
      }
    },
    [absorb, pollMs, timeoutMs],
  );

  // Pressing Send activates the review: confirm with PadSplit straight away.
  useEffect(() => {
    setConfirmedSelection(null);
    setAckFingerprint(null);
    setAddedMembers([]);
    if (!active) {
      runIdRef.current += 1;
      setPhase("idle");
      setMessage(null);
      setAckFingerprint(null);
      setPressedAt(null);
      setElapsedSec(0);
      setAddedMembers([]);
      return;
    }
    const pressed = Date.now();
    setPressedAt(pressed);
    void runConfirmation(pressed);
    return () => {
      runIdRef.current += 1;
      stopRunRef.current();
    };
  }, [active, selectionKey, runConfirmation]);
  useEffect(() => () => { runIdRef.current += 1; stopRunRef.current(); }, []);

  const keepWaiting = useCallback(() => {
    const pressed = Date.now();
    setPressedAt(pressed);
    setElapsedSec(0);
    void runConfirmation(pressed);
  }, [runConfirmation]);

  const confirming = phase === "checking" || phase === "refreshing" || phase === "waiting";
  const confirmed = active && phase === "confirmed" && confirmedSelection === selectionKey;
  const checkReady = query.isSuccess && !query.isFetching && !!data;
  const needsAck = !!data && (data.gapCount > 0 || !confirmed);
  const canSend = checkReady && confirmed && (!needsAck || acknowledged);
  /** Compatibility field: no confirmation path may send without a final action. */
  const readyToAutoSend = false;

  return {
    query,
    data,
    acknowledged,
    setAcknowledged: (v: boolean) => setAckFingerprint(v && data ? data.fingerprint : null),
    phase,
    message,
    elapsedSec,
    pressedAt,
    addedMembers,
    keepWaiting,
    confirming,
    confirmed,
    needsAck,
    checkReady,
    canSend,
    readyToAutoSend,
  };
}

export type BroadcastRosterReviewState = ReturnType<typeof useBroadcastRosterReview>;

function GapRow({ gap, statusFiltered }: { gap: RosterGap; statusFiltered: boolean }) {
  const label =
    gap.kind === "omitted_member" && statusFiltered ? "Excluded by status filter" : GAP_LABELS[gap.kind];
  const detail =
    gap.kind === "omitted_member"
      ? `${gap.memberName ?? "A member"}${gap.roomNumber ? ` (room ${gap.roomNumber})` : ""} will not receive this broadcast.`
      : gap.detail;
  return (
    <li className="text-xs flex gap-2" data-testid={`roster-gap-${gap.kind}-${gap.memberId ?? gap.roomId ?? "house"}`}>
      <span className="shrink-0 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 px-1.5 py-0.5 font-medium">
        {label}
      </span>
      <span className="text-foreground/80">
        {(gap.memberName || gap.roomNumber) && <strong className="block">
          {[gap.memberName, gap.roomNumber ? `Room ${gap.roomNumber.replace(/^room\s*/i, "")}` : null].filter(Boolean).join(" · ")}
        </strong>}
        {detail}
        {gap.kind === "duplicate_room" && <span className="block">Check each resident’s room and current tenancy on the Members page, then refresh and check again. A shared room alone does not verify either resident’s identity.</span>}
      </span>
    </li>
  );
}

const HOUSE_STATE_LABEL: Record<HouseConfirmState, string> = {
  confirmed: "Confirmed with PadSplit",
  waiting: "Waiting for PadSplit…",
  no_data: "No members on record",
};

export function BroadcastRosterReviewPanel({
  review,
  statusFilteredMemberIds,
  onBack,
}: {
  review: BroadcastRosterReviewState;
  statusFilteredMemberIds: ReadonlySet<number>;
  onBack: () => void;
}) {
  const { query, data, phase } = review;
  return (
    <div className="border rounded-md p-3 space-y-3 bg-muted/30" data-testid="broadcast-roster-review">
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm font-medium">Resident check</div>
        <button type="button" className="text-xs text-blue-600 hover:underline" onClick={onBack} data-testid="button-roster-review-back">
          {review.confirming ? "Stop and edit (nothing is sent)" : "Back to edit"}
        </button>
      </div>

      {review.confirming && (
        <div
          className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/40 p-2.5 text-sm text-blue-900 dark:text-blue-200"
          role="status"
          data-testid="roster-confirming"
        >
          <Loader2 className="h-4 w-4 mt-0.5 shrink-0 animate-spin" />
          <div className="space-y-0.5">
            <div className="font-medium">Checking residents with PadSplit — nothing sent yet.</div>
            <div className="text-xs">
              {phase === "refreshing"
                ? "Asking PadSplit for the current resident list…"
                : phase === "waiting"
                  ? `Waiting for PadSplit to report each house · ${formatElapsed(review.elapsedSec)}. This check stops within 75 seconds.`
                  : "Checking each house's roster…"}{" "}
              Nothing is sent during this check. Review the results before submitting.
            </div>
          </div>
        </div>
      )}

      {review.addedMembers.length > 0 && (
        <div className="flex items-start gap-2 text-sm text-green-800 dark:text-green-300" data-testid="roster-added-members">
          <UserPlus className="h-4 w-4 mt-0.5 shrink-0" />
          <span>
            PadSplit reported {review.addedMembers.length} additional resident{review.addedMembers.length === 1 ? "" : "s"} for review (not automatically selected):{" "}
            {review.addedMembers.map((m) => `${m.memberName}${m.roomNumber ? ` (room ${m.roomNumber})` : ""}`).join(", ")}.
          </span>
        </div>
      )}

      {(phase === "timeout" || phase === "error") && review.message && (
        <div className="space-y-2 border-t pt-2" data-testid="roster-confirm-unfinished">
          <p className="text-sm text-amber-800 dark:text-amber-300" data-testid="text-roster-confirm-message">{review.message}</p>
          <p className="text-xs text-muted-foreground">Sending remains blocked. Review the residents and rooms listed below. “Accept risk” cannot override missing identity or fresh roster evidence. Correct the links and refresh, or go back and explicitly deselect the affected residents if they should not receive this message.</p>
          <Button size="sm" variant="outline" onClick={review.keepWaiting} data-testid="button-roster-keep-waiting">Try resident check again</Button>
        </div>
      )}

      {query.isError ? (
        <div className="flex items-start gap-2 text-sm text-red-700 dark:text-red-400" data-testid="roster-check-error">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <div className="space-y-2">
            <div>
              Couldn't check who lives at these houses, so sending is blocked.{" "}
              {parseErrorMessage(query.error, "")}
            </div>
            <Button size="sm" variant="outline" onClick={review.keepWaiting} data-testid="button-roster-check-retry">
              Try again
            </Button>
          </div>
        </div>
      ) : !data ? (phase === "timeout" || phase === "error" ? null :
        <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="roster-check-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Checking each house's roster…
        </div>
      ) : (
        <>
          {data.properties.map((p) => {
              const houseState = review.confirmed && review.pressedAt != null ? houseConfirmState(p, Date.now()) : null;
            return (
              <div key={p.propertyId} className="space-y-1" data-testid={`roster-house-${p.propertyId}`}>
                <div className="text-sm">
                  <span className="font-medium">{p.propertyName ?? `Property #${p.propertyId}`}</span>
                  <span className="text-muted-foreground">
                    {" "}· Roster check covers {p.recipientMemberIds.length} member{p.recipientMemberIds.length === 1 ? "" : "s"} · {p.bedroomCount} bedroom{p.bedroomCount === 1 ? "" : "s"}
                  </span>
                  {houseState && (
                    <span
                      className={`ml-2 text-xs ${houseState === "confirmed" ? "text-green-700 dark:text-green-400" : houseState === "waiting" ? "text-blue-700 dark:text-blue-300" : "text-amber-700 dark:text-amber-300"}`}
                      data-testid={`roster-house-state-${p.propertyId}`}
                    >
                      {houseState === "confirmed" && <CheckCircle2 className="inline h-3.5 w-3.5 mr-0.5 -mt-0.5" />}
                      {HOUSE_STATE_LABEL[houseState]}
                    </span>
                  )}
                </div>
                {p.gaps.length > 0 && (
                  <ul className="space-y-1 pl-1">
                    {p.gaps.map((g, i) => (
                      <GapRow
                        key={`${g.kind}-${g.roomId}-${g.memberId}-${i}`}
                        gap={g}
                        statusFiltered={g.memberId != null && statusFilteredMemberIds.has(g.memberId)}
                      />
                    ))}
                  </ul>
                )}
              </div>
            );
          })}

          {review.confirmed && data.gapCount === 0 ? (
            <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-400" data-testid="roster-check-clear">
              <CheckCircle2 className="h-4 w-4" /> The selected recipients are confirmed with PadSplit. Residents you deselected will not receive this message; this does not confirm their membership.
            </div>
          ) : review.confirmed && review.needsAck ? (
            <div className="space-y-2 border-t pt-2">
              {data.gapCount > 0 && (
                <div className="flex items-start gap-2 text-sm text-amber-800 dark:text-amber-300">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>
                    {data.gapCount} gap{data.gapCount === 1 ? "" : "s"} found. Someone may not get this message.
                  </span>
                </div>
              )}
              <label className="flex items-start gap-2 text-sm cursor-pointer" data-testid="label-roster-acknowledge">
                <Checkbox
                  checked={review.acknowledged}
                  onCheckedChange={(v) => review.setAcknowledged(v === true)}
                  disabled={query.isFetching}
                  data-testid="checkbox-roster-acknowledge"
                />
                <span>
                  {data.gapCount > 0 && !review.confirmed
                    ? "I've reviewed these gaps and want to send without PadSplit's confirmation."
                    : data.gapCount > 0
                      ? "I've reviewed these gaps and want to send anyway."
                      : "Send without PadSplit's confirmation."}
                </span>
              </label>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
